import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ImageGenerationJob } from '../../services/api/imageGenerationService';
import { Clock, Loader2, CheckCircle, XCircle, ChevronRight, RefreshCw, X } from 'lucide-react';
import { shouldShowPrompt } from '../../utils/environment';
import { selectOrganization } from '../../redux/organization/organizationSlice';

interface GeneratingJobCardProps {
  job: ImageGenerationJob;
  onClick: () => void;
  onRegenerate?: (job: ImageGenerationJob) => void;
  /** Promise を返して reject した場合はカードを戻してエラーを出す。 */
  onDelete?: (jobId: string) => void | Promise<unknown>;
  onCancel?: (jobId: string) => void;
}

const GeneratingJobCard: React.FC<GeneratingJobCardProps> = ({ job, onClick, onRegenerate, onDelete, onCancel }) => {
  const organization = useSelector(selectOrganization);
  const adScoreEnabled = organization?.enable_ad_score ?? false;
  const isMagicResize = job.input_mode === 'magic_resize';
  const canBeCancelled = job.status === 'pending' || job.status === 'generating_prompt' || (job.status === 'processing' && job.completed_count === 0);
  // 削除は押した瞬間にカードを消し（楽観的削除）、ref で同じジョブへの二重 DELETE を止める。
  const deletingRef = useRef(false);
  const [deleted, setDeleted] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleDelete = () => {
    if (!onDelete || deletingRef.current) return;
    deletingRef.current = true;
    setDeleteError('');
    setDeleted(true);
    Promise.resolve(onDelete(job.id)).catch(() => {
      deletingRef.current = false;
      setDeleted(false);
      setDeleteError('ログを削除できませんでした。もう一度お試しください。');
    });
  };
  const getStatusIcon = () => {
    switch (job.status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'generating_prompt':
        return <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />;
      case 'processing':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusText = () => {
    switch (job.status) {
      case 'pending':
        return '待機中';
      case 'generating_prompt':
        return 'AIでプロンプト生成中...';
      case 'processing':
        return `処理中 (${job.completed_count}/${job.count}枚完了)`;
      case 'completed':
        return '完了';
      case 'failed':
        return '失敗';
      default:
        return '';
    }
  };

  // Truncate error message to fit in the card
  const truncateErrorMessage = (message: string, maxLength: number = 50) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  const getProgressBarColor = () => {
    switch (job.status) {
      case 'pending':
        return 'bg-yellow-500';
      case 'generating_prompt':
        return 'bg-purple-500';
      case 'processing':
        return 'bg-blue-500';
      case 'completed':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const truncatePrompt = (text: string, maxLength: number = 60) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (deleted) return null;

  return (
    <div
      onClick={onClick}
      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className="text-sm font-medium text-gray-700">{getStatusText()}</span>
          {isMagicResize && (
            <span className="rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700">
              マジックリサイズ
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {(job.status === 'completed' || job.status === 'failed') && onDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
              className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-red-500 transition-colors"
              title="ログを削除"
              aria-label="ログを削除"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
        <div
          className={`h-2 rounded-full transition-all duration-300 ${getProgressBarColor()}`}
          style={{ width: `${job.progress}%` }}
        />
      </div>

      {deleteError && (
        <p className="text-xs text-red-600 mb-2" role="alert">
          {deleteError}
        </p>
      )}

      {/* Error Message - Show when job failed */}
      {job.status === 'failed' && job.error_message && (
        <p className="text-xs text-red-600 mb-2" title={job.error_message}>
          {truncateErrorMessage(job.error_message)}
        </p>
      )}

      {/* Regenerate Button - Show when job failed */}
      {job.status === 'failed' && onRegenerate && !isMagicResize && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRegenerate(job);
          }}
          className="w-full mb-2 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-md transition-colors"
        >
          <RefreshCw className="w-3 h-3" />
          再生成
        </button>
      )}

      {/* Cancel Button - Show when job can be cancelled */}
      {canBeCancelled && onCancel && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onCancel(job.id);
          }}
          className="w-full mb-2 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-md transition-colors"
        >
          <XCircle className="w-3 h-3" />
          キャンセル
        </button>
      )}

      {/* Prompt Preview - Only shown in staging/local environments */}
      {shouldShowPrompt() && !isMagicResize && (
        <p className="text-sm text-gray-600 mb-2">{truncatePrompt(job.prompt)}</p>
      )}

      {/* Meta Info */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{job.count}{isMagicResize ? 'サイズ' : '枚'}生成</span>
        <div className="flex items-center gap-2">
          {adScoreEnabled && job.status === 'completed' && job.avg_ad_score != null && (
            <span className={`font-medium ${
              job.avg_ad_score >= 80 ? 'text-green-600' :
              job.avg_ad_score >= 50 ? 'text-yellow-600' :
              'text-red-600'
            }`}>
              平均スコア: {job.avg_ad_score}
            </span>
          )}
          <span>
            {new Date(job.created_at).toLocaleTimeString('ja-JP', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      </div>
    </div>
  );
};

export default GeneratingJobCard;
